// The build primes source/front matter and rendered HTML before SSR. In the browser this module
// captures the visible static body before MarketingContentPage first renders. Keep it owned by
// the lazy page rather than importing it from marketing-entry: Rollup must not place this shared
// cache in an entry chunk whose hydrateRoot side effect could execute on the app origin.
export type PrimedMarketingPageMarkdown = { markdownFileText: string; renderedBodyHtml: string | null };

const primedMarkdownByRoute = new Map<string, PrimedMarketingPageMarkdown>();

export function primeLoadedMarketingPage(route: string, primed: PrimedMarketingPageMarkdown): void {
  primedMarkdownByRoute.set(route, primed);
}

export function takePrimedMarketingPageMarkdown(route: string): PrimedMarketingPageMarkdown | null {
  const primed = primedMarkdownByRoute.get(route) ?? null;
  primedMarkdownByRoute.delete(route);
  return primed;
}

// The generator (scripts/generate-marketing-static-html.mjs) puts a content page's Markdown in the page.
const MARKDOWN_DATA_SCRIPT_SELECTOR = 'script[type="application/json"][data-marketing-page-markdown]';
function readRenderedBodyHtmlFromDocument(): string | null {
  const renderedBodyParts = Array.from(document.querySelectorAll("#root [data-marketing-rendered-body]"));
  return renderedBodyParts.length > 0 ? renderedBodyParts.map(renderedBodyPart => renderedBodyPart.innerHTML).join("") : null;
}

function primeMarketingPageMarkdownFromDocument() {
  const markdownDataScript = document.querySelector(MARKDOWN_DATA_SCRIPT_SELECTOR);
  if (!markdownDataScript?.textContent) return;
  try {
    const { route, markdown, bodyHtml } = JSON.parse(markdownDataScript.textContent) as {
      route: string;
      markdown: string;
      bodyHtml?: string | null;
    };
    if (typeof route === "string" && typeof markdown === "string") {
      primeLoadedMarketingPage(route, {
        markdownFileText: markdown,
        // Reuse the visible server-rendered body instead of downloading it again
        // inside JSON. Keep compatibility with already generated older HTML.
        // A template may show the body in more than one part (splitMarketingBody.ts); together they are the whole body.
        renderedBodyHtml: typeof bodyHtml === "string" ? bodyHtml : readRenderedBodyHtmlFromDocument(),
      });
    }
  } catch {
    // The page then fetches its Markdown as it would without the embedded copy.
  }
}


if (typeof document !== "undefined") primeMarketingPageMarkdownFromDocument();
